import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { UndoToastContext, type UndoableAction } from './undoToastContext';

export interface UndoableActionApi {
  /**
   * Defers a write behind an undo ticket. The caller owns the optimistic UI —
   * hide the row first, then schedule.
   */
  schedule: (action: UndoableAction) => void;
}

/**
 * Schedules undoable writes and guarantees the ones this component started are
 * committed when it unmounts (tab switch, route change). A pending write is
 * never silently dropped; the worst case is that it commits early.
 */
export function useUndoableAction(): UndoableActionApi {
  const context = useContext(UndoToastContext);
  const ownIds = useRef(new Set<string>());
  const scheduleOnHost = context?.schedule;
  const commitNow = context?.commitNow;

  const schedule = useCallback(
    (action: UndoableAction) => {
      if (!scheduleOnHost) {
        throw new Error('useUndoableAction must be used inside <UndoToastProvider>.');
      }
      // Assigned by `scheduleOnHost` below, read only once the toast settles.
      let id = '';
      const settle = () => ownIds.current.delete(id);
      id = scheduleOnHost({
        ...action,
        onCommit: () => {
          settle();
          return action.onCommit();
        },
        onUndo: () => {
          settle();
          action.onUndo();
        },
      });
      ownIds.current.add(id);
    },
    [scheduleOnHost],
  );

  useEffect(() => {
    if (!commitNow) return;
    const ids = ownIds.current;
    return () => {
      for (const id of [...ids]) commitNow(id);
      ids.clear();
    };
  }, [commitNow]);

  const api = useMemo(() => ({ schedule }), [schedule]);

  // Checked after the hooks, so the hook order never depends on the provider.
  if (!context) {
    throw new Error('useUndoableAction must be used inside <UndoToastProvider>.');
  }

  return api;
}
